import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Download, ExternalLink, FileQuestion, Loader2, Pencil, Trash2, X } from "lucide-react";
import { API, Button, apiFetch, cx } from "../../../pages/admin/_shared";
import { IMAGE_EXT, OFFICE_EXT, TYPE_META, VIDEO_EXT, fmtBytes, fmtDate, iconOfExt } from "./mediaUtils";

const USAGE_TYPE = { location: "Địa điểm", category: "Danh mục", setting: "Cài đặt", user: "Người dùng" };

function Preview({ item }) {
  const { ext, url, name } = item;
  const [broken, setBroken] = useState(false);
  const Icon = iconOfExt(ext) || FileQuestion;
  if (IMAGE_EXT.includes(ext) && !broken) {
    return (
      <div className="flex min-h-[180px] items-center justify-center bg-[conic-gradient(#f1f5f9_25%,#fff_0_50%,#f1f5f9_0_75%,#fff_0)] bg-[length:16px_16px]">
        <img src={url} alt={name} onError={() => setBroken(true)} className="max-h-72 w-full object-contain" referrerPolicy="no-referrer" />
      </div>
    );
  }
  if (VIDEO_EXT.includes(ext)) return <video src={url} controls className="max-h-72 w-full bg-black" />;
  if (ext === "pdf") return <iframe title="PDF" src={url} className="h-72 w-full bg-white" />;
  if (OFFICE_EXT.includes(ext)) {
    return <iframe title="Office" src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`} className="h-72 w-full bg-white" />;
  }
  return (
    <div className="flex h-40 flex-col items-center justify-center gap-2 bg-admin-muted text-admin-textMuted">
      <Icon size={32} strokeWidth={1.4} />
      <span className="text-xs">Không có bản xem trước</span>
    </div>
  );
}

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-2 text-[13px]">
    <dt className="shrink-0 text-admin-textMuted">{label}</dt>
    <dd className="min-w-0 break-words text-right font-medium text-admin-text">{children}</dd>
  </div>
);

/** Panel chi tiết bên phải: xem trước, thông tin, URL, nơi đang sử dụng và thao tác. */
export default function MediaDetailPanel({ item, onClose, onCopy, onRename, onDelete, canWrite, canDelete, refreshKey }) {
  const [usage, setUsage] = useState({ loading: true, total: 0, usages: [], truncated: false });

  useEffect(() => {
    let off = false;
    setUsage({ loading: true, total: 0, usages: [], truncated: false });
    apiFetch(`${API}/media/usage`, { method: "POST", body: JSON.stringify({ keys: [item.key] }) })
      .then((r) => !off && setUsage({ loading: false, total: r.total || 0, usages: r.usages || [], truncated: r.truncated }))
      .catch(() => !off && setUsage({ loading: false, total: 0, usages: [], error: true }));
    return () => {
      off = true;
    };
  }, [item.key, refreshKey]);

  const meta = TYPE_META[item.category] || TYPE_META.other;

  return (
    <aside className="flex h-full flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-admin-border px-4 py-3">
        <h3 className="min-w-0 truncate text-sm font-semibold text-admin-text" title={item.name}>
          {item.name}
        </h3>
        <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-admin-textMuted hover:bg-admin-muted" aria-label="Đóng">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="overflow-hidden border-b border-admin-border">
          <Preview item={item} />
        </div>
        <div className="space-y-4 p-4">
          <div className="flex gap-2">
            <Button variant="primary" size="sm" icon={Copy} className="flex-1" onClick={() => onCopy(item.url)}>
              Copy URL
            </Button>
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-admin-border bg-admin-surface px-3 text-xs font-semibold text-admin-text shadow-sm hover:bg-admin-muted">
              <ExternalLink size={14} /> Mở
            </a>
            <a href={item.url} download={item.name} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-admin-border bg-admin-surface text-admin-text shadow-sm hover:bg-admin-muted" aria-label="Tải xuống" title="Tải xuống">
              <Download size={14} />
            </a>
          </div>

          <dl className="divide-y divide-admin-border rounded-xl border border-admin-border px-3">
            <Row label="Loại">
              <span className={cx("rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1", meta.tone)}>
                {meta.label} · .{item.ext || "?"}
              </span>
            </Row>
            <Row label="Dung lượng">{fmtBytes(item.size)}</Row>
            <Row label="Ngày tải lên">{fmtDate(item.lastModified, true)}</Row>
            <Row label="Thư mục">
              <span className="font-mono text-xs">/{item.key.split("/").slice(0, -1).join("/")}</span>
            </Row>
          </dl>

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-admin-textMuted">URL</p>
            <button type="button" onClick={() => onCopy(item.url)} className="block w-full truncate rounded-lg bg-admin-muted px-3 py-2 text-left font-mono text-[11px] text-admin-text hover:bg-admin-border/60" title="Bấm để copy">
              {item.url}
            </button>
          </div>

          <div>
            <p className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-admin-textMuted">
              Đang được dùng ở
              {usage.loading ? <Loader2 size={12} className="animate-spin" /> : <span className={cx("rounded-full px-1.5 text-[11px] normal-case", usage.total ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600")}>{usage.total ? `${usage.total} nơi` : "Chưa dùng"}</span>}
            </p>
            {usage.error ? <p className="text-xs text-rose-600">Không kiểm tra được mức sử dụng.</p> : null}
            {!usage.loading && !usage.total && !usage.error ? <p className="rounded-lg bg-admin-muted px-3 py-2 text-xs text-admin-textMuted">Chưa có nội dung nào dùng tệp này — an toàn để xoá.</p> : null}
            {usage.usages.length ? (
              <ul className="divide-y divide-admin-border rounded-xl border border-admin-border">
                {usage.usages.map((u, i) => (
                  <li key={`${u.id}-${u.path}-${i}`}>
                    <Link to={u.link} className="flex items-center gap-2 px-3 py-2 hover:bg-admin-muted">
                      <span className="shrink-0 rounded bg-admin-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase text-admin-textMuted">{USAGE_TYPE[u.type] || u.type}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium text-admin-text">{u.name}</span>
                        <span className="block truncate text-[11px] text-admin-textMuted">{u.field}</span>
                      </span>
                      <ExternalLink size={12} className="shrink-0 text-admin-textMuted" />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
            {usage.truncated ? <p className="mt-1 text-[11px] text-admin-textMuted">Chỉ hiện {usage.usages.length} / {usage.total} vị trí đầu.</p> : null}
          </div>
        </div>
      </div>
      {canWrite || canDelete ? (
        <div className="flex gap-2 border-t border-admin-border p-3">
          {canWrite ? (
            <Button size="sm" icon={Pencil} className="flex-1" onClick={() => onRename(item)}>
              Đổi tên
            </Button>
          ) : null}
          {canDelete ? (
            <Button size="sm" variant="danger" icon={Trash2} className="flex-1" onClick={() => onDelete([item])}>
              Xoá
            </Button>
          ) : null}
        </div>
      ) : null}
    </aside>
  );
}
